import React from "react";
import PropTypes from "prop-types";
import { Box, Flex, Modal, Text, useMantineTheme } from "@mantine/core";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { useStylesModalCreate } from "../utils/mantine/stampTabContent/useStylesModalCreate";

const UpdateSubscriptionModal = ({
  opened,
  onClose,
  previewData,
  onConfirm,
  loading,
  success = false,
  error = null,
  paymentError = false,
  onUpdatePayment = null,
  paymentMethodUpdated = false,
  paymentUpdateLoading = false,
}) => {
  const theme = useMantineTheme();
  const { classes: classesModalCreate } = useStylesModalCreate();

  const { preview, current_quantity, new_quantity } = previewData || {};

  // Extract relevant info from preview
  const immediateTransaction = preview?.immediate_transaction;
  const nextTransaction = preview?.next_transaction;
  const recurringTransaction = preview?.recurring_transaction_details;

  // Format currency
  const formatAmount = (amount, currency = "USD") => {
    if (!amount) return "$0.00";
    const currencyCode = currency || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(amount / 100);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
      closeOnClickOutside={!loading && !success && !paymentMethodUpdated}
      closeOnEscape={!loading && !success && !paymentMethodUpdated}
    >
      {paymentMethodUpdated ? (
        <>
          <Box className={classesModalCreate.modalConfirmationTitle}>
            ✓ Payment Method Updated!
          </Box>
          <Text className="modal-text-pinned">
            Your payment method has been updated successfully.
          </Text>
        </>
      ) : success ? (
        <>
          <Box className={classesModalCreate.modalConfirmationTitle}>
            ✓ Subscription Updated!
          </Box>
          <Text className="modal-text-pinned">
            Your subscription has been updated to {new_quantity} index
            {new_quantity !== 1 ? "es" : ""}.
          </Text>
        </>
      ) : error ? (
        <>
          {paymentError && onUpdatePayment ? (
            <>
              <Text
                className={classesModalCreate.modalText}
                mb={theme.margin.base}
              >
                {error}
              </Text>

              <Box className={classesModalCreate.modalConfirmationTitle}>
                Please update your payment method to continue.
              </Box>

              <Flex
                justify={theme.flex.justify.center}
                gap={theme.flex.gap.large}
                mb={theme.margin.xl}
              >
                <CustomButton
                  text="Cancel"
                  componentClasses="buttonWhite"
                  onClick={onClose}
                  disabled={paymentUpdateLoading}
                />
                <CustomButton
                  text={
                    paymentUpdateLoading
                      ? "Loading..."
                      : "Update Payment Method"
                  }
                  componentClasses="buttonBlack modal-button-submit"
                  onClick={onUpdatePayment}
                  disabled={paymentUpdateLoading}
                />
              </Flex>
            </>
          ) : (
            <>
              <Text
                className={classesModalCreate.modalText}
                mb={theme.margin.base}
              >
                {error}
              </Text>

              <Flex
                justify={theme.flex.justify.center}
                gap={theme.flex.gap.large}
                mb={theme.margin.xl}
              >
                <CustomButton
                  text="Close"
                  componentClasses="buttonBlack"
                  onClick={onClose}
                />
              </Flex>
            </>
          )}
        </>
      ) : (
        <>
          <Text className={classesModalCreate.modalText} mb={theme.margin.base}>
            Change from <Text component="b">{current_quantity}</Text> to{" "}
            <Text component="b">{new_quantity}</Text> index
            {new_quantity !== 1 ? "es" : ""}
          </Text>

          {immediateTransaction && immediateTransaction.details?.totals?.total && (
            <Text
              className={classesModalCreate.modalText}
              mb={theme.margin.base}
            >
              Immediate Charge:{" "}
              <Text component="b">
                {formatAmount(
                  immediateTransaction.details?.totals?.total,
                  preview?.currency_code || immediateTransaction.currency_code,
                )}
              </Text>
              <br />
              <Text
                component="span"
                classNames={{ root: "modal-text-unpinned" }}
              >
                Prorated for remaining period
              </Text>
            </Text>
          )}

          {(nextTransaction?.details?.totals?.total ||
            recurringTransaction?.totals?.total) && (
            <Text
              className={classesModalCreate.modalText}
              mb={theme.margin.base}
            >
              Next Billing:{" "}
              <Text component="b">
                {formatAmount(
                  nextTransaction?.details?.totals?.total ||
                    recurringTransaction?.totals?.total,
                  preview?.currency_code ||
                    nextTransaction?.currency_code ||
                    "USD",
                )}
              </Text>
            </Text>
          )}

          {!immediateTransaction && !nextTransaction && !recurringTransaction && (
            <Text
              className={classesModalCreate.modalText}
              classNames={{ root: "modal-text-unpinned" }}
              mb={theme.margin.base}
            >
              Pricing preview unavailable
            </Text>
          )}

          <Box className={classesModalCreate.modalConfirmationTitle}>
            Update Subscription?
          </Box>

          <Flex
            justify={theme.flex.justify.center}
            gap={theme.flex.gap.large}
            mb={theme.margin.xl}
          >
            <CustomButton
              text="No"
              componentClasses="buttonWhite"
              onClick={onClose}
              disabled={loading}
            />
            <CustomButton
              text={loading ? "Updating..." : "Yes"}
              componentClasses="buttonBlack modal-button-submit"
              onClick={onConfirm}
              disabled={loading}
            />
          </Flex>
        </>
      )}
    </Modal>
  );
};

UpdateSubscriptionModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  previewData: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.string,
  paymentError: PropTypes.bool,
  onUpdatePayment: PropTypes.func,
  paymentMethodUpdated: PropTypes.bool,
  paymentUpdateLoading: PropTypes.bool,
};

export default UpdateSubscriptionModal;
